import type { ReactNode } from 'react'
import { injectStyle } from 'react-toastify/dist/inject-style'
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import type { ToastPosition } from 'react-toastify'
import { Slide, ToastContainer } from 'react-toastify'
import { dismiss, dismissAll, notify, toast, update } from './Toasts'

import * as S from './ToastsProvider.styled'

export type ToastsProviderProps = {
  position?: 'top' | 'bottom'
  autoClose?: number
  children: ReactNode
}

const toastContainerPositionMap: Record<string, ToastPosition> = {
  bottom: 'bottom-center',
  top: 'top-center',
  'top-right': 'top-right',
  'top-left': 'top-left',
  'bottom-right': 'bottom-right',
  'bottom-left': 'bottom-left',
}

const ToastsContext = createContext({
  notify,
  update,
  dismiss,
  dismissAll,
  toast,
})

export const ToastsProvider = ({
  position = 'bottom',
  autoClose = 5000,
  children,
}: ToastsProviderProps) => {
  const value = useMemo(() => ({ notify, update, dismiss, dismissAll, toast }), [])

  useEffect(() => {
    injectStyle()
  }, [])

  return (
    <ToastsContext.Provider value={value}>
      <S.GlobalStyles />
      {children}
      <ToastContainer
        autoClose={autoClose}
        hideProgressBar
        closeButton={false}
        closeOnClick={false}
        draggable={false}
        icon={false}
        position={toastContainerPositionMap[position]}
        // NOTE: we want to remove the role attr here as we have it included
        // in Alert component
        role=""
        transition={Slide}
      />
    </ToastsContext.Provider>
  )
}

export const useToastsContext = () => useContext(ToastsContext)
